import React, { useState } from 'react';
import {
  Sparkles,
  Globe,
  Check,
  X,
  AlertCircle,
  Users,
  Loader2,
  CheckCircle2,
  ChevronDown,
  ChevronUp,
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { Card, CardContent } from '@/components/ui/card';
import { venueRoomService } from '@/lib/api/venue-room.service';
import type { ExtractedRoom, VenueRoomWithConfigs } from '@/types/venue-room.types';
import { ROOM_TYPE_LABELS, SEATING_CONFIG_LABELS } from '@/types/venue-room.types';

type Step = 'url-input' | 'scanning' | 'preview' | 'importing' | 'done' | 'error';

interface AutoDetectRoomsModalProps {
  venueId: number;
  venueWebsite?: string;
  venueName: string;
  onComplete: (rooms: VenueRoomWithConfigs[]) => void;
  onClose: () => void;
}

export const AutoDetectRoomsModal: React.FC<AutoDetectRoomsModalProps> = ({
  venueId,
  venueWebsite,
  venueName,
  onComplete,
  onClose,
}) => {
  const [step, setStep] = useState<Step>('url-input');
  const [url, setUrl] = useState(venueWebsite || '');
  const [extractedRooms, setExtractedRooms] = useState<(ExtractedRoom & { selected: boolean })[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [resultMessage, setResultMessage] = useState('');
  const [importedCount, setImportedCount] = useState(0);
  const [expandedRoom, setExpandedRoom] = useState<number | null>(null);
  const [deepScan, setDeepScan] = useState(false);

  const handleScan = async () => {
    if (!url.trim()) {
      setError('Please enter a website URL');
      return;
    }

    // Basic URL validation
    try {
      new URL(url.trim());
    } catch {
      setError('Please enter a valid URL (e.g., https://example.com/event-spaces)');
      return;
    }

    setError(null);
    setStep('scanning');

    try {
      const result = await venueRoomService.autoDetectRooms(venueId, url.trim(), { deepScan });
      const roomsWithSelection = (result.rooms || []).map(room => ({
        ...room,
        selected: (room.confidence ?? 0.5) >= 0.3, // Auto-select rooms with decent confidence
      }));

      setExtractedRooms(roomsWithSelection);
      setResultMessage(result.message);

      if (roomsWithSelection.length === 0) {
        setStep('error');
        setError(result.message || 'No rooms or spaces could be identified from the website. Try a different page URL.');
      } else {
        setStep('preview');
      }
    } catch (err: any) {
      const msg = err?.response?.data?.message || err?.response?.data?.error || err?.message || 'Failed to scan website';
      setError(msg);
      setStep('error');
    }
  };

  const handleToggleRoom = (index: number) => {
    setExtractedRooms(prev =>
      prev.map((room, i) => (i === index ? { ...room, selected: !room.selected } : room))
    );
  };

  const handleToggleAll = () => {
    const allSelected = extractedRooms.every(r => r.selected);
    setExtractedRooms(prev => prev.map(room => ({ ...room, selected: !allSelected })));
  };

  const handleImport = async () => {
    const selectedRooms = extractedRooms.filter(r => r.selected);
    if (selectedRooms.length === 0) {
      setError('Please select at least one room to import');
      return;
    }

    setError(null);
    setStep('importing');

    try {
      // Strip UI-only fields before sending to API
      const roomsToCreate = selectedRooms.map(({ selected: _s, confidence: _c, source_url: _u, ...room }) => room);
      
      // Deduplicate rooms by name (case-insensitive) to prevent accidental duplicates
      const seenNames = new Set<string>();
      const deduplicatedRooms = roomsToCreate.filter(room => {
        const nameLower = room.name.toLowerCase();
        if (seenNames.has(nameLower)) {
          return false;
        }
        seenNames.add(nameLower);
        return true;
      });

      const createdRooms = await venueRoomService.bulkCreateRooms(venueId, deduplicatedRooms);
      setImportedCount(createdRooms.length);
      setStep('done');

      // Notify parent after a short delay to show success state
      setTimeout(() => {
        onComplete(createdRooms);
      }, 1500);
    } catch (err: any) {
      const msg = err?.response?.data?.message || err?.response?.data?.error || 'Failed to import rooms';
      setError(msg);
      setStep('preview'); // Go back to preview so user can retry
    }
  };

  const selectedCount = extractedRooms.filter(r => r.selected).length;

  const getConfidenceBadge = (confidence?: number) => {
    const c = confidence ?? 0.5;
    if (c >= 0.8) return <Badge className="bg-green-100 text-green-700 text-[10px]">High confidence</Badge>;
    if (c >= 0.5) return <Badge className="bg-yellow-100 text-yellow-700 text-[10px]">Medium confidence</Badge>;
    return <Badge className="bg-orange-100 text-orange-700 text-[10px]">Low confidence</Badge>;
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center" role="dialog" aria-modal="true">
      <div className="fixed inset-0 bg-black/40" onClick={onClose} />
      <div
        className="relative bg-white rounded-xl shadow-2xl w-full max-w-2xl max-h-[85vh] flex flex-col"
        onClick={e => e.stopPropagation()}
        data-testid="auto-detect-modal"
      >
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b">
          <div className="flex items-center gap-2">
            <Sparkles className="h-5 w-5 text-purple-600" />
            <h2 className="text-lg font-semibold text-gray-900">Auto-Detect Rooms</h2>
          </div>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 p-1 rounded-md hover:bg-gray-100"
            aria-label="Close"
          >
            <X className="h-5 w-5" />
          </button>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto px-6 py-4">
          {/* Step 1: URL Input */}
          {step === 'url-input' && (
            <div className="space-y-4">
              <p className="text-sm text-gray-600">
                Enter the venue website URL to automatically detect rooms and event spaces using AI.
                For best results, use the page that lists the venue's event spaces or rooms.
              </p>
              <div className="space-y-2">
                <label className="text-sm font-medium text-gray-700">Website URL</label>
                <div className="flex gap-2">
                  <div className="relative flex-1">
                    <Globe className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
                    <Input
                      value={url}
                      onChange={e => setUrl(e.target.value)}
                      placeholder="https://venue-website.com/event-spaces"
                      className="pl-10"
                      data-testid="auto-detect-url-input"
                      onKeyDown={e => e.key === 'Enter' && handleScan()}
                    />
                  </div>
                </div>
                {venueWebsite && url !== venueWebsite && (
                  <button
                    onClick={() => setUrl(venueWebsite)}
                    className="text-xs text-blue-600 hover:text-blue-800"
                  >
                    Use venue's website: {venueWebsite}
                  </button>
                )}
              </div>
              {error && (
                <div className="flex items-center gap-2 text-sm text-red-600 bg-red-50 p-3 rounded-md">
                  <AlertCircle className="h-4 w-4 flex-shrink-0" />
                  {error}
                </div>
              )}

              <label className="flex items-start gap-2 rounded-md border border-slate-200 bg-slate-50 p-3">
                <input
                  type="checkbox"
                  checked={deepScan}
                  onChange={(e) => setDeepScan(e.target.checked)}
                  className="mt-0.5 h-4 w-4 rounded border-slate-300 text-purple-600 focus:ring-purple-500"
                />
                <span className="text-xs text-slate-700">
                  Enable deep scan (slower): uses broader AI web-search fallback if this page does not include event-space details.
                </span>
              </label>
            </div>
          )}

          {/* Step 2: Scanning */}
          {step === 'scanning' && (
            <div className="flex flex-col items-center justify-center py-12 space-y-4">
              <div className="relative">
                <Loader2 className="h-10 w-10 text-purple-600 animate-spin" />
                <Sparkles className="h-4 w-4 text-purple-400 absolute -top-1 -right-1 animate-pulse" />
              </div>
              <div className="text-center">
                <p className="text-sm font-medium text-gray-900">
                  Analyzing website for rooms & spaces...
                </p>
                <p className="text-xs text-gray-500 mt-1">
                  {deepScan
                    ? `Running deep scan for ${url} (may take longer)`
                    : `Running fast scan for ${url}`}
                </p>
              </div>
            </div>
          )}

          {/* Step 3: Preview */}
          {step === 'preview' && (
            <div className="space-y-4">
              <div className="flex items-center justify-between">
                <p className="text-sm text-gray-600">{resultMessage}</p>
                <button
                  onClick={handleToggleAll}
                  className="text-xs text-blue-600 hover:text-blue-800 font-medium"
                >
                  {extractedRooms.every(r => r.selected) ? 'Deselect all' : 'Select all'}
                </button>
              </div>

              {error && (
                <div className="flex items-center gap-2 text-sm text-red-600 bg-red-50 p-3 rounded-md">
                  <AlertCircle className="h-4 w-4 flex-shrink-0" />
                  {error}
                </div>
              )}

              <div className="space-y-2">
                {extractedRooms.map((room, index) => (
                  <Card
                    key={index}
                    className={`transition-all cursor-pointer ${
                      room.selected
                        ? 'ring-2 ring-purple-300 bg-purple-50/30'
                        : 'opacity-60 hover:opacity-80'
                    }`}
                    data-testid={`extracted-room-${index}`}
                  >
                    <CardContent className="pt-3 pb-3">
                      <div className="flex items-start gap-3">
                        {/* Selection checkbox */}
                        <button
                          onClick={() => handleToggleRoom(index)}
                          className={`mt-0.5 flex-shrink-0 h-5 w-5 rounded border-2 flex items-center justify-center transition-colors ${
                            room.selected
                              ? 'bg-purple-600 border-purple-600 text-white'
                              : 'border-gray-300 hover:border-purple-400'
                          }`}
                          data-testid={`room-checkbox-${index}`}
                        >
                          {room.selected && <Check className="h-3 w-3" />}
                        </button>

                        {/* Room info */}
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2 flex-wrap">
                            <span className="font-medium text-sm">{room.name}</span>
                            {room.room_type && (
                              <Badge variant="outline" className="text-[10px]">
                                {ROOM_TYPE_LABELS[room.room_type as keyof typeof ROOM_TYPE_LABELS] || room.room_type}
                              </Badge>
                            )}
                            {getConfidenceBadge(room.confidence)}
                          </div>

                          {room.description && (
                            <p className="text-xs text-gray-600 mt-1 line-clamp-2">{room.description}</p>
                          )}

                          <div className="flex flex-wrap items-center gap-3 mt-1.5 text-xs text-gray-500">
                            {room.max_capacity && (
                              <span className="flex items-center gap-1">
                                <Users className="h-3 w-3" />
                                Up to {room.max_capacity}
                              </span>
                            )}
                            {room.floor_area_sqft && <span>{room.floor_area_sqft} sq ft</span>}
                            {room.has_stage && <span>Stage</span>}
                            {room.has_dance_floor && <span>Dance Floor</span>}
                            {room.has_outdoor_access && <span>Outdoor</span>}
                            {room.has_av_equipment && <span>AV</span>}
                          </div>

                          {/* Seating configurations (visible by default if available) */}
                          {room.seating_configs && room.seating_configs.length > 0 && (
                            <div className="mt-2 text-xs">
                              <span className="text-gray-600 font-medium block mb-1">Seating Configurations:</span>
                              <div className="flex flex-wrap gap-1.5">
                                {room.seating_configs.map((sc, i) => (
                                  <Badge key={i} variant="secondary" className="text-[10px] bg-blue-50 text-blue-700 border-blue-200">
                                    {SEATING_CONFIG_LABELS[sc.config_type as keyof typeof SEATING_CONFIG_LABELS] || sc.config_type}
                                    {sc.capacity ? ` (${sc.capacity})` : ''}
                                  </Badge>
                                ))}
                              </div>
                            </div>
                          )}

                          {/* Expandable details */}
                          {(room.has_natural_light || room.has_kitchen_access || room.has_wheelchair_access || room.has_climate_control) && (
                            <button
                              onClick={(e) => {
                                e.stopPropagation();
                                setExpandedRoom(expandedRoom === index ? null : index);
                              }}
                              className="flex items-center gap-1 text-xs text-gray-400 hover:text-gray-600 mt-2"
                            >
                              {expandedRoom === index ? (
                                <><ChevronUp className="h-3 w-3" /> Less details</>
                              ) : (
                                <><ChevronDown className="h-3 w-3" /> More details</>
                              )}
                            </button>
                          )}

                          {expandedRoom === index && (
                            <div className="mt-2 space-y-1.5 text-xs text-gray-500">
                              {/* Additional Amenities */}
                              <div className="flex flex-wrap gap-1">
                                {room.has_natural_light && <Badge variant="secondary" className="text-[10px]">Natural Light</Badge>}
                                {room.has_wheelchair_access && <Badge variant="secondary" className="text-[10px]">Accessible</Badge>}
                                {room.has_climate_control && <Badge variant="secondary" className="text-[10px]">Climate Control</Badge>}
                                {room.has_kitchen_access && <Badge variant="secondary" className="text-[10px]">Kitchen Access</Badge>}
                              </div>
                            </div>
                          )}
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>
            </div>
          )}

          {/* Step 4: Importing */}
          {step === 'importing' && (
            <div className="flex flex-col items-center justify-center py-12 space-y-4">
              <Loader2 className="h-10 w-10 text-purple-600 animate-spin" />
              <p className="text-sm font-medium text-gray-900">
                Importing {selectedCount} room{selectedCount !== 1 ? 's' : ''}...
              </p>
            </div>
          )}

          {/* Step 5: Done */}
          {step === 'done' && (
            <div className="flex flex-col items-center justify-center py-12 space-y-4">
              <div className="h-12 w-12 bg-green-100 rounded-full flex items-center justify-center">
                <CheckCircle2 className="h-7 w-7 text-green-600" />
              </div>
              <div className="text-center">
                <p className="text-sm font-medium text-gray-900">
                  Successfully imported {importedCount} room{importedCount !== 1 ? 's' : ''}!
                </p>
                <p className="text-xs text-gray-500 mt-1">
                  The rooms have been added to {venueName}
                </p>
              </div>
            </div>
          )}

          {/* Error state (with retry) */}
          {step === 'error' && (
            <div className="flex flex-col items-center justify-center py-12 space-y-4">
              <div className="h-12 w-12 bg-red-100 rounded-full flex items-center justify-center">
                <AlertCircle className="h-7 w-7 text-red-600" />
              </div>
              <div className="text-center max-w-sm">
                <p className="text-sm font-medium text-gray-900">
                  Could not detect rooms
                </p>
                <p className="text-xs text-gray-500 mt-1">
                  {error}
                </p>
              </div>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="px-6 py-4 border-t bg-gray-50 rounded-b-xl flex items-center justify-between">
          <div className="text-xs text-gray-500">
            {step === 'preview' && `${selectedCount} of ${extractedRooms.length} rooms selected`}
          </div>
          <div className="flex gap-2">
            {step === 'url-input' && (
              <>
                <Button variant="outline" size="sm" onClick={onClose}>
                  Cancel
                </Button>
                <Button
                  size="sm"
                  onClick={handleScan}
                  disabled={!url.trim()}
                  className="bg-purple-600 hover:bg-purple-700 text-white"
                  data-testid="scan-website-btn"
                >
                  <Sparkles className="h-3.5 w-3.5 mr-1.5" />
                  Scan Website
                </Button>
              </>
            )}

            {step === 'preview' && (
              <>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => { setStep('url-input'); setError(null); setExtractedRooms([]); setExpandedRoom(null); }}
                >
                  Back
                </Button>
                <Button
                  size="sm"
                  onClick={handleImport}
                  disabled={selectedCount === 0}
                  className="bg-purple-600 hover:bg-purple-700 text-white"
                  data-testid="import-rooms-btn"
                >
                  <Check className="h-3.5 w-3.5 mr-1.5" />
                  Import {selectedCount} Room{selectedCount !== 1 ? 's' : ''}
                </Button>
              </>
            )}

            {step === 'error' && (
              <>
                <Button variant="outline" size="sm" onClick={onClose}>
                  Close
                </Button>
                <Button
                  size="sm"
                  onClick={() => { setStep('url-input'); setError(null); setExtractedRooms([]); setExpandedRoom(null); }}
                  className="bg-purple-600 hover:bg-purple-700 text-white"
                >
                  Try Again
                </Button>
              </>
            )}

            {step === 'done' && (
              <Button size="sm" onClick={onClose}>
                Done
              </Button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};
